import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useEmbeddedEffect } from '@machinemetrics/mm-react-embeddable';
import { IconButton } from '@chakra-ui/react';
import { palette, Icon } from '@m12s/component-library';
import MMEmbeddableZoneWithLoader from 'components/MMEmbeddableZoneWithLoader';
import MMDrawer from 'components/MMDrawer';
import { APM_SELECT_JOBS_HOST } from 'lib/appConfig';
import { getMachine } from 'lib/selectors/getMachine';
import { arrowLeft } from 'lib/icons';

const EmbeddedSelectJobs = ({
  id,
  machineRef,
  eventId,
  machine,
  onClose,
  onStepChanged,
  requestedStep,
  onJobSelected,
}) => {
  useEmbeddedEffect(id, 'apm-select-jobs:cancel', () => {
    onClose();
  });

  useEmbeddedEffect(
    id,
    'apm-select-jobs:job-selected',
    useCallback(
      (data) => {
        if (onJobSelected) {
          onJobSelected(data);
        } else {
          onClose();
        }
      },
      [onClose, onJobSelected]
    )
  );

  useEmbeddedEffect(
    id,
    'apm-select-jobs:step-changed',
    useCallback(
      (data) => {
        onStepChanged(data);
      },
      [onStepChanged]
    )
  );

  return (
    <MMEmbeddableZoneWithLoader
      id={id}
      src={APM_SELECT_JOBS_HOST}
      path="/"
      context={{
        machineRef,
        machineId: machine?.id,
        eventId,
        erpResources: machine?.erpResources,
        requestedStep,
        selectionMode: 'single',
      }}
    />
  );
};

EmbeddedSelectJobs.propTypes = {
  id: PropTypes.string.isRequired,
  machineRef: PropTypes.number,
  eventId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  machine: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    erpResources: PropTypes.arrayOf(PropTypes.string),
  }),
  onClose: PropTypes.func.isRequired,
  onStepChanged: PropTypes.func.isRequired,
  requestedStep: PropTypes.string,
  onJobSelected: PropTypes.func,
};

const SelectJobsDrawer = ({ isOpen, onClose, eventId, onJobSelected }) => {
  const machine = useSelector(getMachine);
  const id = 'apm-select-jobs';
  const [drawerTitle, setDrawerTitle] = useState('Select Jobs');
  const [showBack, setShowBack] = useState(false);
  const [requestedStep, setRequestedStep] = useState(null);

  const handleStepChanged = useCallback((data) => {
    if (data?.step === 'prepare') {
      setDrawerTitle(data?.title || 'Preparing jobs');
      setShowBack(true);
    } else {
      setDrawerTitle('Select Jobs');
      setShowBack(false);
    }
    // Clear any pending requestedStep once MFE confirms the step change
    setRequestedStep(null);
  }, []);

  const handleBack = useCallback(() => {
    setRequestedStep('select');
    setDrawerTitle('Select Jobs');
    setShowBack(false);
  }, []);

  const handleClose = useCallback(() => {
    setDrawerTitle('Select Jobs');
    setShowBack(false);
    setRequestedStep(null);
    onClose();
  }, [onClose]);

  if (!isOpen) {
    return null;
  }

  const backButton = showBack ? (
    <IconButton
      aria-label="Back"
      icon={<Icon size="1rem" color={palette.Grey700} icon={arrowLeft} />}
      variant="outline"
      size="sm"
      borderColor={palette.Grey400}
      borderRadius="4px"
      w="40px"
      h="40px"
      minW="40px"
      bg="transparent"
      _hover={{ bg: palette.Grey200 }}
      _focus={{ boxShadow: 'none' }}
      onClick={handleBack}
    />
  ) : null;

  return (
    <MMDrawer
      isOpen={isOpen}
      onClose={handleClose}
      title={drawerTitle}
      headerLeft={backButton}
      hideHeader
    >
      <EmbeddedSelectJobs
        id={id}
        machineRef={machine?.machineRef}
        eventId={eventId}
        machine={machine}
        onClose={handleClose}
        onStepChanged={handleStepChanged}
        requestedStep={requestedStep}
        onJobSelected={onJobSelected}
      />
    </MMDrawer>
  );
};

SelectJobsDrawer.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  eventId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onJobSelected: PropTypes.func,
};

export { SelectJobsDrawer };
